/* ============================================================
   shippedTrackingView — G8-D3: pure view model for the Shipped
   tracking delta. 31c9ace7: the checkout carrier snapshot
   (DeliveryCarrier — the tariff the customer picked) is the
   authoritative label/deep-link source; the tracking-number shape
   (СДЭК = 10 digits, Почта России = 14 digits) stays the legacy
   fallback. Also builds the «Отследить» deep link plus
   delivery-method-aware copy
   (самовывоз vs курьер via the G8-D1 DeliveryMethod snapshot).
   Zero runtime imports — plain data in, plain data out — so the
   module runs under `node --test` (tests/shippedTrackingView.test.ts)
   as well as in the app bundle. No API change (spec row 3,
   docs/delivery-scoping-spec.md §5). Light theme, Montserrat,
   iOS-18 minimal per DESIGN-SYSTEM.md (link inherits the global
   `a` primary-color style).
   ============================================================ */

import type { WebDeliveryMethod } from '../types/product';

export type TrackingCarrier = 'Cdek' | 'RussianPost';

export interface ShippedTrackingView {
  /** Trimmed tracking number; null when absent/blank. */
  trackingNumber: string | null;
  /** Carrier detected by number shape; null = unknown shape (no link). */
  /** 31c9ace7: checkout snapshot (canonical key or pass-through name); shape
    * guess for legacy orders; null = neither known (no link). */
  carrier: TrackingCarrier | string | null;
  /** «СДЭК» / «Почта России» — labels the number for the customer. */
  carrierLabel: string | null;
  /** Carrier public tracking page with the number pre-filled; null when unknown. */
  trackUrl: string | null;
  /** «Отследить» link copy; null when no carrier link is possible. */
  trackLabel: string | null;
  /** True for самовывоз/пункт выдачи — the parcel travels to a point, not an address. */
  isPickup: boolean;
  /** Method-aware handoff sentence for the Shipped banner (G8-D1 field). */
  handoffText: string;
}

/** СДЭК: order number is exactly 10 digits. */
const CDEK_SHAPE = /^\d{10}$/;
/** Почта России: RPO number is exactly 14 digits. */
const POST_SHAPE = /^\d{14}$/;

function detectCarrier(trimmed: string): TrackingCarrier | null {
  if (CDEK_SHAPE.test(trimmed)) return 'Cdek';
  if (POST_SHAPE.test(trimmed)) return 'RussianPost';
  return null;
}

/** TASK-248: shared pickup predicate — самовывоз/ПВЗ orders end in a
 *  collection handoff, so waiting/shipping copy must not say «отправлен».
 *  Courier — including legacy rows without a method snapshot — travels
 *  to the address (false = courier copy). */
export function isPickupDeliveryMethod(method?: WebDeliveryMethod | number | null): boolean {
  return (
    method === 'Pickup'
    || method === 'DropoffPoint'
    || method === 0
    || method === 1
  );
}

/** Pure derivation — trivially unit-testable, no React dependency. */
export function buildShippedTrackingView(input: {
  trackingNumber?: string | null;
  /** G8-D1 snapshot on the wire (numeric enum tolerated; absent/legacy = Courier). */
  deliveryMethod?: WebDeliveryMethod | number | null;
  /** 31c9ace7: checkout carrier snapshot («СДЭК», «CDEK», «Почта России»,
   *  «Ozon», …) — the tariff the customer picked, so it wins over the
   *  shape guess; unknown names still label the row (honest, no link).
   *  Absent/null = legacy order, shape detection as before. */
  deliveryCarrier?: string | null;
}): ShippedTrackingView {
  const trimmed = (input.trackingNumber ?? '').trim();
  const trackingNumber = trimmed.length > 0 ? trimmed : null;
  const snapshot = normalizeCarrierLabel(input.deliveryCarrier);
  // 31c9ace7: snapshot first (authoritative — chosen at checkout), then the
  // legacy shape guess; without a number there is nothing to resolve.
  const carrier = trackingNumber == null
    ? null
    : snapshot != null
      ? snapshot
      : detectCarrier(trackingNumber);

  const isPickup = isPickupDeliveryMethod(input.deliveryMethod);

  const carrierLabel = carrier === 'Cdek' ? 'СДЭК' : carrier === 'RussianPost' ? 'Почта России' : carrier === 'Ozon' ? 'Ozon' : carrier;
  const trackUrl =
    carrier === 'Cdek' && trackingNumber != null
      ? `https://www.cdek.ru/ru/tracking?order_id=${trackingNumber}`
      : carrier === 'RussianPost' && trackingNumber != null
        ? `https://www.pochta.ru/tracking#${trackingNumber}`
        // Ozon (and any other carrier): no public number-only tracking page —
        // label without a fabricated link until the Ozon integration spec
        // (1d7f2f57) lands (same honest rule as the backend email).
        : null;

  return {
    trackingNumber,
    carrier,
    carrierLabel,
    trackUrl,
    trackLabel: trackUrl != null ? 'Отследить' : null,
    isPickup,
    handoffText: isPickup
      ? 'Заказ передан перевозчику — получить его можно будет в пункте самовывоза.'
      : 'Заказ передан перевозчику — курьер доставит его по указанному адресу.',
  };
}

/** 31c9ace7: normalizes a checkout carrier snapshot to the canonical key
 *  («Cdek» / «RussianPost» / «Ozon»); unrecognized names pass through trimmed
 *  (they still label the row — honest, no link); empty → null. Mirrors
 *  backend EmailTemplates.NormalizeCarrierLabel so email and web agree. */
export function normalizeCarrierLabel(deliveryCarrier?: string | null): TrackingCarrier | string | null {
  const trimmed = (deliveryCarrier ?? '').trim();
  if (trimmed.length === 0) return null;
  const lower = trimmed.toLowerCase();
  if (lower.includes('сдэк') || lower.includes('cdek')) return 'Cdek';
  if (lower.includes('почт')) return 'RussianPost';
  if (lower.includes('ozon') || lower.includes('озон')) return 'Ozon';
  return trimmed;
}
